import React, { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors } from 'context';
import { careerType } from 'services/careerType';
import { useGrowth, useMyCareerCenter, useShare } from 'core';

import { GrowthAchievements as MedalGrid } from 'components/pages/Growth/parts/GrowthAchievements';
import { GrowthLevelBar } from 'components/pages/Growth/parts/GrowthLevelBar';
import { GrowthPromoCodes } from 'components/pages/Growth/parts/GrowthPromoCodes';
import { GrowthShare } from 'components/pages/Growth/parts/GrowthShare';

/**
 * /growth/achievements — «Баллы и медали» (ТЗ 0039, доводка UX).
 *
 * Отдельная страница появилась по прямой претензии владельца: «у нас есть
 * здесь оценки — вынести на отдельную страницу, расписать, для чего это вообще
 * всё нужно. Сейчас ты ученик, а потом что?»
 *
 * Ответ у системы был и раньше: пять уровней заданы кортежем GROWTH_LEVELS,
 * потолок — 315 очков. Показывалась ровно одна ступень, поэтому лестница
 * выглядела тупиком.
 *
 * 🔴 Очки НИЧЕГО НЕ ЗАПИРАЮТ — так решил владелец: «награды, а не запреты».
 * Вакансии, курсы и карта навыков открыты всем участникам независимо от
 * уровня. Поэтому текст обещает скидку и статус, а не доступ: обещать доступ,
 * которого механизм не отнимает, значит соврать на первом же экране.
 */
export const GrowthMedals = memo(() => {
  const { t, i18n } = useTranslation();
  const { growth, loading } = useGrowth();
  const { isPreview } = useMyCareerCenter();
  // Адрес школы приходит с сервера вместе с вариантами репоста — одно место,
  // где он задаётся. Язык передаём ТОТ ЖЕ, что и `GrowthShare` внутри: с
  // разными переменными Apollo считает это разными запросами.
  const { options: shareOptions } = useShare(i18n.language === 'ru' ? 'RUS' : 'POL');
  const siteUrl = shareOptions[0]?.targetUrl ?? '';

  // Тот же гард, что на /growth: раздел открыт студенту и методисту-смотрящему,
  // остальным — 404, а не полупустой экран с молчаливыми отказами.

  const [showLadder, setShowLadder] = useState(false);
  const [showWhy, setShowWhy] = useState(false);

  const earned = useMemo(
    () => (growth?.achievements ?? []).filter((row) => row.earned).length,
    [growth],
  );

  // Спиннер — только пока данных НЕТ. `centerLoading` истинен при каждом
  // фоновом обновлении (`cache-and-network`), и рамка раздела уже решила, что
  // кэша хватает, — ребёнок не должен отменять это решение полноэкранным
  // ожиданием.
  if (loading && !growth) {
    return (
      <Center>
        <Spin size="large" />
      </Center>
    );
  }

  if (!growth) {
    return (
      <PageWrapper>
        <Title>{t('growth.medals.title')}</Title>
        <Muted>{t('growth.empty')}</Muted>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <header>
        <Title>{t('growth.medals.title')}</Title>
        <Lead>{t('growth.medals.lead')}</Lead>
      </header>

      {/* 0072: порядок — уровень и ближайшая цель → медали → награды →
          лестница и объяснение по раскрытию. Раньше объяснения и лестница
          стояли до самих достижений, а число баллов повторялось трижды. */}
      <GrowthLevelBar growth={growth} />

      <MedalGrid
        achievements={growth.achievements}
        isCareerMember={growth.isCareerMember}
        availableTotal={growth.availableTotal}
        lockedTotal={growth.lockedTotal}
      />

      {/* ТЗ 0069: промокоды и репост — рядом с наградами, а не на плане. */}
      {isPreview ? null : <GrowthPromoCodes siteUrl={siteUrl} />}
      <GrowthShare readOnly={isPreview} />

      <Disclosure>
        <DisclosureButton
          type="button"
          aria-expanded={showLadder}
          aria-controls="growth-ladder"
          onClick={() => setShowLadder((value) => !value)}>
          {t('growth.medals.ladderTitle')}
          <Muted>
            {t('growth.medals.ladderSummary', {
              level: t(`growth.level.${growth.level}`, { defaultValue: growth.levelTitle }),
              earned,
              total: growth.availableTotal,
            })}
          </Muted>
        </DisclosureButton>
        <div id="growth-ladder" hidden={!showLadder}>
          <SectionLead>{t('growth.medals.ladderLead')}</SectionLead>
          <Ladder>
            {growth.levels.map((level) => {
              const isCurrent = level.index === growth.level;
              return (
                <Step key={level.index} reached={level.reached} current={isCurrent}>
                  <Rung reached={level.reached} current={isCurrent}>
                    {level.index + 1}
                  </Rung>
                  <StepBody>
                    <StepName>
                      {t(`growth.level.${level.index}`, { defaultValue: level.title })}
                    </StepName>
                    <StepThreshold>{t('growth.medals.from', { n: level.threshold })}</StepThreshold>
                    {isCurrent ? <Here>{t('growth.medals.youAreHere')}</Here> : null}
                  </StepBody>
                </Step>
              );
            })}
          </Ladder>
        </div>
      </Disclosure>

      <Disclosure>
        <DisclosureButton
          type="button"
          aria-expanded={showWhy}
          aria-controls="growth-why"
          onClick={() => setShowWhy((value) => !value)}>
          {t('growth.medals.whyTitle')}
        </DisclosureButton>
        <div id="growth-why" hidden={!showWhy}>
          <Reasons>
            <Reason>
              <ReasonTitle>{t('growth.medals.why1Title')}</ReasonTitle>
              <ReasonText>{t('growth.medals.why1Text')}</ReasonText>
            </Reason>
            <Reason>
              <ReasonTitle>{t('growth.medals.why2Title')}</ReasonTitle>
              <ReasonText>{t('growth.medals.why2Text')}</ReasonText>
            </Reason>
            <Reason>
              <ReasonTitle>{t('growth.medals.why3Title')}</ReasonTitle>
              <ReasonText>{t('growth.medals.why3Text')}</ReasonText>
            </Reason>
          </Reasons>
          {/* Прямо: очки не отнимают доступ. */}
          <NoGate>{t('growth.medals.noGate')}</NoGate>
        </div>
      </Disclosure>
    </PageWrapper>
  );
});

const Disclosure = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 16px;
  border-top: 1px solid var(--career-line, #e5e7eb);
`;

const DisclosureButton = styled.button`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 12px;
  width: 100%;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${Colors.black};
  text-align: left;
  cursor: pointer;
  ${fontService.getFont({ size: 20, lineHeight: 140, weight: FontWeight.medium })}
`;

const PageWrapper = styled.div`
  width: 100%;
  padding: 40px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 28px;

  ${mobile} {
    padding: 20px 0 48px;
  }
`;

const Title = styled.h1`
  margin: 0;
  ${careerType.h1()};
`;

const Lead = styled.p`
  margin: 8px 0 0;
  max-width: 70ch;
  color: ${Colors.gray_dark};
  ${careerType.body()};
`;

const SectionLead = styled.p`
  margin: 6px 0 16px;
  max-width: 720px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Ladder = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
`;

const Step = styled.li<{ reached: boolean; current: boolean }>`
  display: flex;
  gap: 16px;
  padding: 0 0 20px;
  position: relative;

  /* Вертикальная линия лестницы: рисуется псевдоэлементом от ступени вниз,
     у последней — обрезается, иначе линия висит в пустоте. */
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 40px;
    bottom: 0;
    width: 2px;
    background: ${({ reached }) => (reached ? Colors.marker_green : Colors.gray)};
  }
`;

const Rung = styled.span<{ reached: boolean; current: boolean }>`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Текущая ступень — жёлтая ЗАЛИВКА и btn_text поверх (правило в
     context/constants/colors.ts: black в тёмной теме равен #ECECEC и на
     жёлтом исчезает).

     Пройденная — зелёная РАМКА и обычный текст, а не заливка с белым: белый
     на marker_green читается в светлой теме и почти пропадает в тёмной, где
     тот же токен светлеет до #12B577. Рамка от темы не зависит. */
  background: ${({ current }) => (current ? Colors.main : Colors.bg_gray)};
  color: ${({ reached, current }) =>
    current ? Colors.btn_text : reached ? Colors.black : Colors.gray_dark};
  border: 1px solid
    ${({ reached, current }) =>
      current ? 'transparent' : reached ? Colors.signal_green : Colors.gray};
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const StepBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-top: 6px;
`;

const StepName = styled.span`
  ${fontService.text({ type: TextType.medium, weight: FontWeight.bold })}
`;

const StepThreshold = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Here = styled.span`
  margin-top: 4px;
  align-self: flex-start;
  padding: 2px 10px;
  border-radius: 12px;
  background: ${Colors.main};
  color: ${Colors.btn_text};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Reasons = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${mobile} {
    grid-template-columns: 1fr;
  }
`;

const Reason = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReasonTitle = styled.span`
  ${fontService.text({ type: TextType.small, weight: FontWeight.bold })}
`;

const ReasonText = styled.span`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const NoGate = styled.p`
  margin: 0;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${Colors.bg_gray};
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Muted = styled.p`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;
